import AWS from 'aws-sdk';
import { DatabaseAsleepError } from '@/helpers/databaseAsleepError';
import store from '../store/index';

export default class LeanCMSAdminSDK {
  awsExports = {
    identity_pool_id: '',
    s3_bucket_prefix: '',
    aws_project_region: '',
    Auth: {
      region: '',
      userPoolId: '',
      userPoolWebClientId: ''
    },
    Storage: {
      AWSS3: {
        systemBucket: '',
        unpublishedBucket: '',
        publishedBucket: ''
      }
    }
  };
  s3 = new AWS.S3();
  sqsTriggerPublisherUrl = '';
  lambda = new AWS.Lambda();
  sqs = new AWS.SQS();
  cognito = new AWS.CognitoIdentityServiceProvider({
    apiVersion: '2016-04-18'
  });

  constructor(awsExports) {
    if (!awsExports) throw new Error('CRITICAL ERROR: no aws exports found!');
    this.awsExports = awsExports;
    this.s3 = new AWS.S3({
      apiVersion: '2006-03-01',
      params: { Bucket: awsExports.Storage.AWSS3.systemBucket }
    });
  }

  getDatatypeFromPath(path) {
    return path.split('/').slice(1, 2).pop();
  }
  getSlugFromPath(path) {
    return path.split('/').slice(2, 3).pop();
  }
  getLanguageFromPath(path) {
    return path.split('/').slice(3, 4).pop();
  }

  getDataRootPath(datatype, slug, language) {
    return `data/${datatype}/${slug}/${language}`;
  }

  getWorkspaceBucket() {
    const workspace = store.getters.currentWorkspace;
    return this.awsExports.s3_bucket_prefix + workspace;
  }

  getDocument(path) {
    return this.get(this.getWorkspaceBucket(), path);
  }

  headDocument(path) {
    return this.head(this.getWorkspaceBucket(), path);
  }

  getSignedUrl(path) {
    var params = {
      Bucket: this.getWorkspaceBucket(),
      Key: path,
      Expires: 3600
    };
    return this.s3.getSignedUrl('getObject', params);
  }

  getSignedDownloadUrl(path, filename) {
    var params = {
      Bucket: this.getWorkspaceBucket(),
      Key: path,
      Expires: 3600,
      ResponseContentDisposition: `attachment; filename="${filename}"`
    };
    return this.s3.getSignedUrl('getObject', params);
  }

  get(bucket, path) {
    return this.s3
      .getObject({
        Bucket: bucket,
        Key: path
      })
      .promise()
      .then((data) => {
        return JSON.parse(data.Body.toString());
      })
      .catch((err) => {
        console.error(`There was an error getting ${path} from bucket ${bucket} - ` + err.message);
        throw err;
      });
  }

  /**
   * Makes a HTTP-HEAD request, which returns all GET headers without retrieving the file.
   */
  head(bucket, path) {
    return this.s3
      .headObject({
        Bucket: bucket,
        Key: path
      })
      .promise()
      .then((data) => {
        return data;
      })
      .catch((err) => {
        console.error(`There was an error getting ${path} from bucket ${bucket} - ` + err.message);
        throw err;
      });
  }

  putObjectToDataBucket(path, body) {
    return this.putObjectToBucket({ bucket: this.getWorkspaceBucket(), path: 'data/' + path, body });
  }

  putObjectToBucket({ bucket, path, body }) {
    return this.s3
      .putObject({
        Bucket: bucket,
        Key: path,
        Body: body
      })
      .promise();
  }

  // Use S3 ManagedUpload class as it supports multipart uploads
  putFileToBucket({ bucket, path, file, progressListener }) {
    if (progressListener) {
      return this.s3
        .upload({ Bucket: bucket, Key: path, Body: file })
        .on('httpUploadProgress', progressListener)
        .promise();
    }
    return this.s3.upload({ Bucket: bucket, Key: path, Body: file }).promise();
  }

  putFileToDataBucket(path, file) {
    // FIXME: path needs possible change to 'data/' prefix
    return this.putFileToBucket({ bucket: this.getWorkspaceBucket(), path, file });
  }

  getDocuments(options) {
    const payload = {
      bucket: this.getWorkspaceBucket(),
      datatype: options.datatype ?? '',
      language: options.language,
      fields: options.fields,
      limit: options.limit,
      offset: options.offset,
      order: options.order,
      slug: options.slug,
      sort: options.sort,
      query: options.query
    };
    if (options.destinationBucket) {
      payload.destinationBucket = options.destinationBucket;
    }
    if (options.filenames) {
      payload.filenames = options.filenames;
    }
    const params = {
      FunctionName: 'leancms-admin',
      InvocationType: 'RequestResponse',
      LogType: 'Tail',
      Payload: JSON.stringify(payload)
    };
    return this.lambda
      .invoke(params)
      .promise()
      .then((response) => {
        let payload;
        try {
          payload = JSON.parse(response.Payload);
        } catch (err) {
          throw err;
        }
        if (payload.error && payload.error.retryAllowed) {
          throw new DatabaseAsleepError('Database not responding');
        } else if (payload.error) {
          throw new Error(response.Payload);
        } else {
          return payload;
        }
      });
  }

  deleteObjectFromBucket({ bucket, path }) {
    return this.s3.deleteObject({ Bucket: bucket, Key: path }).promise();
  }

  deleteObjectFromWorkspace(path) {
    return this.deleteObjectFromBucket({ bucket: this.getWorkspaceBucket(), path });
  }

  getObjectVersions(bucket, path) {
    return this.s3.listObjectVersions({ Bucket: bucket, Prefix: path }).promise();
  }

  getLastWorkspaceVersion(path) {
    return this.getLastVersionFromObject(this.getWorkspaceBucket(), path);
  }

  getLastVersionFromObject(bucket, path) {
    return this.getObjectVersions(bucket, path).then((data) => {
      // after self checking the versions are in correct chronological order
      return data.Versions[0];
    });
  }

  publishObject(src, dest, datatype, slug, language, version) {
    const params = {
      QueueUrl: this.sqsTriggerPublisherUrl,
      MessageBody: JSON.stringify({
        src: src,
        dest: dest,
        datatype: datatype,
        slug: slug,
        language: language,
        version: version
      })
    };
    return this.sqs
      .sendMessage(params)
      .promise()
      .then(() => {
        return true;
      })
      .catch((reason) => {
        console.error(reason);
        return false;
      });
  }

  getSQSPublisherUrl() {
    const prefix = this.awsExports.s3_bucket_prefix.split('-') ?? [''];
    if (prefix.length > 1) {
      return this.sqs
        .getQueueUrl({ QueueName: `${prefix[0]}-trigger-publisher` })
        .promise()
        .then((value) => (this.sqsTriggerPublisherUrl = value.QueueUrl));
    }
    return '';
  }

  getAllFilesByPath(bucket, path) {
    return this.s3
      .listObjectsV2({
        Bucket: bucket,
        Prefix: path
      })
      .promise();
  }

  getAllFilesFromWorkspaceByPath(path) {
    return this.getAllFilesByPath(this.getWorkspaceBucket(), path);
  }

  getFileTagging(bucket, path) {
    return this.s3.getObjectTagging({ Bucket: bucket, Key: path }).promise();
  }

  getWorkspaceFileTagging(path) {
    return this.getFileTagging(this.getWorkspaceBucket(), path).then((data) => {
      data.path = path;
      return data;
    });
  }

  getPublishTagFromCurrentWorkspaceFile(path) {
    return this.getWorkspaceFileTagging(path).then((data) =>
      (data.TagSet ?? []).find((item) => item.Key === 'published')
    );
  }

  getUsers() {
    var params = {
      UserPoolId: this.awsExports.Auth.userPoolId,
      AttributesToGet: ['email']
    };
    return this.cognito
      .listUsers(params)
      .promise()
      .then((data) => {
        return data.Users.map((user) => {
          return {
            slug: user.Attributes[0].Value,
            email: user.Attributes[0].Value,
            enabled: user.Enabled,
            createDate: user.UserCreateDate,
            lastModifiedDate: user.UserLastModifiedDate,
            status: user.UserStatus,
            name: user.Username
          };
        });
      });
  }

  updateUser(username, email) {
    var params = {
      UserPoolId: this.awsExports.Auth.userPoolId,
      UserAttributes: [
        {
          Name: 'email',
          Value: email
        }
      ],
      Username: username
    };
    return this.cognito
      .adminUpdateUserAttributes(params)
      .promise()
      .then((data) => {
        console.log(data);
      });
  }

  enableUser(username) {
    var params = {
      UserPoolId: this.awsExports.Auth.userPoolId,
      Username: username
    };
    return this.cognito
      .adminEnableUser(params)
      .promise()
      .then((data) => {
        console.log(data);
      });
  }

  disableUser(username) {
    var params = {
      UserPoolId: this.awsExports.Auth.userPoolId,
      Username: username
    };
    return this.cognito
      .adminDisableUser(params)
      .promise()
      .then((data) => {
        console.log(data);
      });
  }

  deleteUser(username) {
    var params = {
      UserPoolId: this.awsExports.Auth.userPoolId,
      Username: username
    };
    return this.cognito
      .adminDeleteUser(params)
      .promise()
      .then((data) => {
        console.log(data);
      });
  }

  resetPassword(username) {
    var params = {
      UserPoolId: this.awsExports.Auth.userPoolId,
      Username: username
    };
    return this.cognito
      .adminResetUserPassword(params)
      .promise()
      .then((data) => {
        console.log(data);
      });
  }

  createUser(username, email) {
    var params = {
      UserPoolId: this.awsExports.Auth.userPoolId,
      Username: username,
      DesiredDeliveryMediums: ['EMAIL'],
      ForceAliasCreation: false,
      // MessageAction: 'RESEND',
      UserAttributes: [
        {
          Name: 'email',
          Value: email
        },
        {
          Name: 'email_verified',
          Value: 'True'
        }
      ]
    };
    return this.cognito
      .adminCreateUser(params)
      .promise()
      .then((data) => {
        console.log(data);
      });
  }
}
